import { useState } from "react";
import mammoth from "mammoth";
import { toast } from "react-toastify";
import { uploadImage, createArticle } from "./supportApi";
import { compressImage } from "../../lib/imageCompression";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { X, Upload, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

const STATUS = {
  PARSING: "parsing",
  READY: "ready",
  UPLOADING: "uploading",
  DONE: "done",
  ERROR: "error",
};

export default function BulkArticleUpload({
  availableCategories,
  onClose,
  onComplete,
}) {
  const [queueItems, setQueueItems] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const updateItem = (id, updates) => {
    setQueueItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
    );
  };

  const parseDocx = async (file) => {
    const arrayBuffer = await file.arrayBuffer();

    // Upload each embedded image and return its server URL
    const imageConverter = mammoth.images.imgElement(async (image) => {
      try {
        const buffer = await image.readAsArrayBuffer();
        const ext = (image.contentType || "image/jpeg").split("/")[1] || "jpg";
        const blob = new Blob([buffer], { type: image.contentType });
        let imageFile = new File([blob], `upload.${ext}`, {
          type: image.contentType,
        });
        imageFile = await compressImage(imageFile);
        const result = await uploadImage(imageFile, "article");
        // Handle different server response shapes
        const url =
          result?.url ||
          result?.path ||
          result?.src ||
          result?.imageUrl ||
          result?.data?.url ||
          "";
        return { src: url };
      } catch {
        return { src: "" };
      }
    });

    const result = await mammoth.convertToHtml(
      { arrayBuffer },
      { convertImage: imageConverter },
    );
    return result.value;
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const defaultSection = availableCategories[0]?.id || "gidex";

    const newItems = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      title: file.name.replace(/\.docx$/i, ""),
      productSection: defaultSection,
      useForAI: true,
      status: STATUS.PARSING,
      htmlContent: "",
      error: "",
    }));

    setQueueItems((prev) => [...prev, ...newItems]);

    // Reset input so same files can be re-selected if needed
    e.target.value = "";

    for (const item of newItems) {
      try {
        const html = await parseDocx(item.file);
        updateItem(item.id, { status: STATUS.READY, htmlContent: html });
      } catch (err) {
        updateItem(item.id, {
          status: STATUS.ERROR,
          error: "Failed to parse file",
        });
      }
    }
  };

  const handleUploadAll = async () => {
    const readyItems = queueItems.filter((i) => i.status === STATUS.READY);
    if (!readyItems.length) return;

    setIsUploading(true);

    for (const item of readyItems) {
      updateItem(item.id, { status: STATUS.UPLOADING });
      try {
        const articleData = {
          productSection: item.productSection,
          en: { title: item.title, content: item.htmlContent },
          useForAI: item.useForAI,
        };
        const response = await createArticle(articleData);
        if (response.ok || response.status === 200 || response.status === 201) {
          updateItem(item.id, { status: STATUS.DONE });
        } else {
          const err = await response.json().catch(() => ({}));
          updateItem(item.id, {
            status: STATUS.ERROR,
            error: err.message || err.error || "Upload failed",
          });
        }
      } catch (err) {
        updateItem(item.id, {
          status: STATUS.ERROR,
          error: err.message || "Upload failed",
        });
      }
    }

    setIsUploading(false);

    const doneNow = queueItems.filter((i) => i.status === STATUS.DONE).length;
    if (doneNow > 0) {
      toast.success(
        `${doneNow} article${doneNow > 1 ? "s" : ""} uploaded successfully!`,
      );
      if (onComplete) onComplete();
    }
  };

  const removeItem = (id) => {
    setQueueItems((prev) => prev.filter((item) => item.id !== id));
  };

  const readyCount = queueItems.filter((i) => i.status === STATUS.READY).length;
  const parsingCount = queueItems.filter(
    (i) => i.status === STATUS.PARSING,
  ).length;
  const doneCount = queueItems.filter((i) => i.status === STATUS.DONE).length;

  return (
    <Card className="max-w-5xl shadow-lg bg-white/5 border-[#146b4f]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle
            className="text-2xl font-bold text-white"
            style={{ fontFamily: '"Stargaze Regular", sans-serif' }}
          >
            Bulk Upload Articles
          </CardTitle>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition"
          >
            <X size={20} />
          </button>
        </div>
        <p
          className="text-gray-400 text-sm mt-1"
          style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
        >
          Select multiple .docx files. Images inside docs will be uploaded
          automatically. Every article is created as a <strong>draft</strong> —
          publish them from the article list once they are ready.
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* File Drop Zone */}
        <div className="border-2 border-dashed border-[#146b4f] rounded-lg p-8 text-center">
          <Upload className="mx-auto mb-3 text-[#146b4f]" size={32} />
          <p
            className="text-white text-sm mb-4"
            style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
          >
            Select .docx files to upload
          </p>
          <Input
            type="file"
            accept=".docx"
            multiple
            onChange={handleFilesSelected}
            disabled={isUploading}
            className="max-w-xs mx-auto text-white border bg-[#232323] border-[#146b4f] file:bg-transparent file:border-none file:text-white cursor-pointer"
            style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
          />
        </div>

        {/* Queue Table */}
        {queueItems.length > 0 && (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border bg-[#232323] border-[#146b4f] rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-white/20 border-b border-gray-600">
                    <th
                      className="px-4 py-3 text-left font-semibold text-white text-sm"
                      style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                    >
                      Title
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold text-white text-sm"
                      style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                    >
                      Product Section
                    </th>
                    <th
                      className="px-4 py-3 text-center font-semibold text-white text-sm"
                      style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                    >
                      Use for AI
                    </th>
                    <th
                      className="px-4 py-3 text-center font-semibold text-white text-sm"
                      style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                    >
                      Status
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-white text-sm" />
                  </tr>
                </thead>
                <tbody>
                  {queueItems.map((item) => {
                    const locked =
                      item.status === STATUS.UPLOADING ||
                      item.status === STATUS.DONE;
                    return (
                      <tr
                        key={item.id}
                        className="border-b border-gray-600 hover:bg-white/5 transition"
                      >
                        {/* Title */}
                        <td className="px-4 py-3 min-w-[200px]">
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) =>
                              updateItem(item.id, { title: e.target.value })
                            }
                            disabled={locked}
                            className="w-full bg-transparent text-white text-sm border-b border-[#146b4f]/50 focus:border-[#146b4f] focus:outline-none py-1 disabled:opacity-50"
                            style={{
                              fontFamily: '"Scarlex Regular", sans-serif',
                            }}
                          />
                        </td>

                        {/* Product Section */}
                        <td className="px-4 py-3">
                          <select
                            value={item.productSection}
                            onChange={(e) =>
                              updateItem(item.id, {
                                productSection: e.target.value,
                              })
                            }
                            disabled={locked}
                            className="bg-[#1a1a1a] text-white text-sm border border-[#146b4f] rounded px-2 py-1 disabled:opacity-50"
                            style={{
                              fontFamily: '"Scarlex Regular", sans-serif',
                            }}
                          >
                            {availableCategories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.label}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* KB Checkbox */}
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={item.useForAI}
                            onChange={(e) =>
                              updateItem(item.id, { useForAI: e.target.checked })
                            }
                            disabled={locked}
                            className="w-4 h-4 accent-emerald-500 disabled:opacity-50 cursor-pointer"
                          />
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 text-center">
                          {item.status === STATUS.PARSING && (
                            <span
                              className="text-yellow-400 text-xs flex items-center justify-center gap-1"
                              style={{
                                fontFamily: '"Scarlex Regular", sans-serif',
                              }}
                            >
                              <Loader2 size={13} className="animate-spin" />
                              Parsing...
                            </span>
                          )}
                          {item.status === STATUS.READY && (
                            <span
                              className="text-blue-400 text-xs"
                              style={{
                                fontFamily: '"Scarlex Regular", sans-serif',
                              }}
                            >
                              Ready
                            </span>
                          )}
                          {item.status === STATUS.UPLOADING && (
                            <span
                              className="text-yellow-400 text-xs flex items-center justify-center gap-1"
                              style={{
                                fontFamily: '"Scarlex Regular", sans-serif',
                              }}
                            >
                              <Loader2 size={13} className="animate-spin" />
                              Uploading...
                            </span>
                          )}
                          {item.status === STATUS.DONE && (
                            <span
                              className="text-emerald-400 text-xs flex items-center justify-center gap-1"
                              style={{
                                fontFamily: '"Scarlex Regular", sans-serif',
                              }}
                            >
                              <CheckCircle size={13} />
                              Done
                            </span>
                          )}
                          {item.status === STATUS.ERROR && (
                            <span
                              className="text-red-400 text-xs flex items-center justify-center gap-1"
                              title={item.error}
                              style={{
                                fontFamily: '"Scarlex Regular", sans-serif',
                              }}
                            >
                              <AlertCircle size={13} />
                              Error
                            </span>
                          )}
                        </td>

                        {/* Remove */}
                        <td className="px-4 py-3 text-center">
                          {!locked && (
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-gray-500 hover:text-red-400 transition"
                            >
                              <X size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Upload Button */}
            {readyCount > 0 && (
              <button
                onClick={handleUploadAll}
                disabled={isUploading || parsingCount > 0}
                className={`w-full py-3 rounded-full font-semibold text-sm transition ${
                  isUploading || parsingCount > 0
                    ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
                style={{ fontFamily: '"Astronomus Regular", sans-serif' }}
              >
                {parsingCount > 0
                  ? `Parsing ${parsingCount} file${parsingCount > 1 ? "s" : ""}...`
                  : `Upload All — ${readyCount} Article${readyCount > 1 ? "s" : ""}`}
              </button>
            )}

            {/* All done message */}
            {doneCount > 0 && readyCount === 0 && !isUploading && (
              <div className="text-center space-y-3">
                <p
                  className="text-emerald-400 text-sm"
                  style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
                >
                  {doneCount} article{doneCount > 1 ? "s" : ""} uploaded
                  successfully!
                </p>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-white text-black rounded-full text-sm font-semibold hover:opacity-90 transition"
                  style={{ fontFamily: '"Astronomus Regular", sans-serif' }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
